/**
 * O que a grade custa por mês — D1 do plano.
 *
 * A frase que originou esta tela, dela, no fechamento da reunião de 23/10 e
 * sem ter sido perguntada:
 *
 *     "eu tenho isso de professor com essa classificação, o valor da hora
 *      aula dele é esse, a sua grade está te dando isso de custo. Hoje a
 *      gente não tem. A gente só descobre isso quando eu faço fechamento de
 *      folha, de pagamento. É loucura."
 *
 * **Três decisões de tela, e nenhuma é estética.**
 *
 * 1. **A janela vem antes do total**, apesar de ser 1% dele. O total é
 *    inevitável — são os professores que a instituição tem. A janela é a
 *    parte que a grade decide, e é a única linha sobre a qual montar melhor
 *    muda alguma coisa. Pôr o número grande primeiro enterraria o acionável
 *    debaixo do inerte.
 *
 * 2. **"R$ 0 de janela" não aparece sozinho.** Sem grade montada, zero quer
 *    dizer "ninguém sabe ainda", e não "grade boa". São estados opostos com
 *    o mesmo número, e a tela diz qual é.
 *
 * 3. **Quem está sem valor aparece em bloco próprio, fora do total.** Somá-lo
 *    como zero diria que a grade é mais barata do que é — a direção errada
 *    para errar quando alguém decide contratação com o número.
 *
 * **E a tela diz de onde vem o número** (§23.4). Antes ela se chamava "Custo
 * da grade" e mostrava o custo da planilha de atribuição, com ou sem grade.
 * Agora: sem grade, o número é o planejado, e diz isso; com grade, é o da
 * grade — quem de fato está em cada aula —, com o planejado ao lado. E uma
 * grade com aulas sem lugar parece mais barata porque aula sem lugar não é
 * paga: o aviso vem antes da diferença, não depois.
 */

import { useEffect, useMemo, useState } from 'react'

import { ErroApi, horario, type Cenario, type Custo as CustoDoc } from '../api'
import { Icon } from '../components/Icon'

/** Centavos inteiros → `R$ 1.234,56`. A conversão mora só aqui. */
const reais = (centavos: number) =>
  (centavos / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  })

export function Custo({ projeto }: { projeto: string }) {
  const [cenarios, setCenarios] = useState<Cenario[]>([])
  const [cenario, setCenario] = useState('')
  const [dados, setDados] = useState<CustoDoc | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [carregando, setCarregando] = useState(false)

  useEffect(() => {
    horario
      .cenarios(projeto)
      .then((itens) => {
        setCenarios(itens)
        setCenario((c) => c || (itens[0]?.id ?? ''))
      })
      .catch((e) => setErro(e instanceof ErroApi ? e.message : 'nao foi possivel carregar'))
  }, [projeto])

  useEffect(() => {
    if (!cenario) return
    setCarregando(true)
    horario
      .custo(projeto, cenario)
      .then((d) => {
        setDados(d)
        setErro(null)
      })
      .catch((e) => setErro(e instanceof ErroApi ? e.message : 'nao foi possivel calcular'))
      .finally(() => setCarregando(false))
  }, [projeto, cenario])

  const maior = useMemo(
    () => Math.max(1, ...(dados?.por_classificacao ?? []).map((g) => g.total_mes)),
    [dados],
  )

  return (
    <div className="section">
      <header className="page-head">
        <h1>Custo</h1>
        <p className="page-head__note">
          {dados
            ? `${dados.base === 'GRADE' ? 'da grade montada' : 'do planejado'} · ${dados.linhas.length} ${dados.linhas.length === 1 ? 'professor' : 'professores'} · ${dados.semanas_no_mes} semanas no mês`
            : 'o que a folha de aula custa por mês'}
        </p>
      </header>

      {cenarios.length > 1 && (
        <label className="field">
          <span>Cenário</span>
          <select className="select" value={cenario} onChange={(e) => setCenario(e.target.value)}>
            {cenarios.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nome}
              </option>
            ))}
          </select>
        </label>
      )}

      {erro && (
        <p className="state state--pendencia" role="alert">
          <Icon name="aviso" size={14} />
          {erro}
        </p>
      )}
      {carregando && !dados && <p className="field__hint">Calculando…</p>}

      {dados && (
        <>
          <Origem dados={dados} />

          <div className="custo__totais" data-tour="totais">
            {/* A janela primeiro: é a única linha que montar melhor diminui. */}
            <article className="custo__cartao custo__cartao--foco">
              <h2>Janela</h2>
              <strong className="mono">{reais(dados.janela_mes)}</strong>
              <p className="field__hint">
                {!dados.grade_montada
                  ? 'A grade ainda não foi montada. Zero aqui não quer dizer grade boa: quer dizer que ninguém sabe ainda.'
                  : dados.janela_mes === 0
                    ? 'Nenhum horista ficou com buraco entre aulas.'
                    : 'Hora paga sem aula dada, a horista. Montar melhor diminui esta linha — e só esta.'}
              </p>
            </article>

            <article className="custo__cartao">
              <h2>Aula</h2>
              <strong className="mono">{reais(dados.aula_mes)}</strong>
              <p className="field__hint">
                {dados.base === 'GRADE'
                  ? 'Créditos postos na grade × valor da hora-aula de quem está em cada aula.'
                  : 'Créditos atribuídos × valor da hora-aula de cada um.'}
              </p>
            </article>

            <article className="custo__cartao">
              <h2>Total mensal</h2>
              <strong className="mono">{reais(dados.total_mes)}</strong>
              <p className="field__hint">
                Só aula em sala. Adicional de regime e noturno entram na folha e não aqui.
              </p>
            </article>
          </div>

          {dados.sem_valor.length > 0 && (
            <p className="callout" data-tour="sem-valor">
              <strong>
                {dados.sem_valor.length} professor(es) dão aula e estão sem valor de hora-aula.
              </strong>{' '}
              Ficam <em>fora</em> do total acima — somá-los como zero diria que a grade é mais
              barata do que ela é. Preencha a classificação deles em Professores:{' '}
              {dados.sem_valor
                .slice(0, 6)
                .map((p) => `${p.nome} (${p.aulas_semana} créditos)`)
                .join(', ')}
              {dados.sem_valor.length > 6 ? `, e mais ${dados.sem_valor.length - 6}.` : '.'}
            </p>
          )}

          <div className="section__head">
            <h2>Por classificação</h2>
            <p>onde o dinheiro está, por nível de carreira</p>
          </div>
          <div className="tabela-rolavel">
            <table className="tabela" data-tour="por-classificacao">
              <thead>
                <tr>
                  <th>Classificação</th>
                  <th>Professores</th>
                  <th>Créditos/semana</th>
                  <th>Custo mensal</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {dados.por_classificacao.map((g) => (
                  <tr key={g.classificacao}>
                    <td>{g.classificacao}</td>
                    <td className="mono">{g.professores}</td>
                    <td className="mono">{g.aulas_semana}</td>
                    <td className="mono">{reais(g.total_mes)}</td>
                    <td className="custo__barra-celula">
                      {/* Barra e não gráfico: são seis linhas na mesma tela,
                          e um eixo numerado repetiria a coluna ao lado. */}
                      <span
                        className="custo__barra"
                        style={{ width: `${Math.round((g.total_mes / maior) * 100)}%` }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="section__head">
            <h2>Por professor</h2>
            <p>do mais caro para o menos</p>
          </div>
          <div className="tabela-rolavel">
            <table className="tabela" data-tour="por-professor">
              <thead>
                <tr>
                  <th>Professor</th>
                  <th>Classificação</th>
                  <th>Regime</th>
                  <th>Hora-aula</th>
                  <th>Créditos</th>
                  <th>Janela</th>
                  <th>Mensal</th>
                </tr>
              </thead>
              <tbody>
                {dados.linhas.map((l) => (
                  <tr key={l.professor_id}>
                    <td>{l.professor}</td>
                    <td>{l.classificacao ?? '—'}</td>
                    <td>{l.regime ?? '—'}</td>
                    <td className="mono">
                      {l.valor_hora_aula === null
                        ? '—'
                        : reais(Math.round(l.valor_hora_aula * 100))}
                    </td>
                    <td className="mono">{l.aulas_semana}</td>
                    <td className="mono">{l.janela_semana || '—'}</td>
                    <td className="mono">{reais(l.total_mes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}

/**
 * De onde vem o número, dito antes do número.
 *
 * Sem grade: é o planejado, e a frase diz o que falta para virar o da grade.
 * Com grade: os dois lado a lado, com a diferença — e, se aulas ficaram sem
 * lugar, o aviso vem primeiro, porque é ele que decide se a diferença é
 * economia ou buraco.
 */
function Origem({ dados }: { dados: CustoDoc }) {
  if (!dados.grade) {
    return (
      <p className="callout" data-tour="origem">
        <strong>Este é o custo planejado</strong> — de quem a coordenação decidiu que dá cada
        aula. A grade deste cenário ainda não foi montada; quando for, este número passa a ser o
        dela, e o planejado fica ao lado para comparar.
      </p>
    )
  }

  const { grade, atribuicao } = dados
  const diferenca = dados.diferenca_mes ?? 0
  return (
    <div className="custo__origem" data-tour="origem">
      {grade.aulas_pendentes > 0 && (
        <p className="state state--pendencia" role="status">
          <Icon name="aviso" size={14} />
          {grade.aulas_pendentes} aula(s) ficaram sem lugar nesta grade e não entram na conta. A
          diferença abaixo não é economia enquanto elas não tiverem horário.
        </p>
      )}
      <dl className="custo__comparacao">
        <div>
          <dt>Planejado</dt>
          <dd className="mono">{reais(atribuicao.total_mes)}</dd>
        </div>
        <div>
          <dt>Esta grade</dt>
          <dd className="mono">{reais(grade.total_mes)}</dd>
        </div>
        <div>
          <dt>Diferença</dt>
          <dd className="mono">
            {diferenca > 0 ? '+' : ''}
            {reais(diferenca)}
          </dd>
        </div>
      </dl>
    </div>
  )
}
